import React, { useEffect, useState } from 'react';
import '../../styles/user/bgvCompletedCandidates.css';
import { fetchBgvCompletedCandidates } from '../../services/user/api';
import { Search } from 'lucide-react';
import { FaDownload } from 'react-icons/fa';

const BgvCompletedCandidatesPage = ({ onViewFinalReport }) => {
  const [candidates, setCandidates] = useState([]);
  const [fetchError, setFetchError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(10);

  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');

useEffect(() => {
  loadCandidates(currentPage);
}, [currentPage, entriesPerPage, search]);


  const loadCandidates = async (page) => {
    try {
      setLoading(true);
const response = await fetchBgvCompletedCandidates(
  page,
  entriesPerPage,
  search
);

      if (response?.data?.status) {
        const data = response.data.data;
        setCandidates(data.content || []);
        setTotalElements(data.totalElements || 0);
        setFetchError(null);
      } else {
        setFetchError(response?.data?.message || 'Failed to fetch data.');
      }
    } catch (error) {
      console.error('API Error:', error);
      setFetchError('An error occurred while fetching data.');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const renderSortIcon = (field) => {
    if (sortField !== field) return ' ⇅';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const sortedCandidates = [...candidates].sort((a, b) => {
    if (!sortField) return 0;

    let valA = a[sortField] ?? '';
    let valB = b[sortField] ?? '';

    
    if (
      sortField.toLowerCase().includes('date') ||
      sortField.toLowerCase().includes('dob')
    ) {
      const parseDate = (d) => {
        if (!d) return 0;
        const [day, month, year] = d.split('-'); 
        return new Date(year, month - 1, day).getTime();
      };
      valA = parseDate(valA);
      valB = parseDate(valB);
    } else {
      valA = String(valA).toLowerCase();
      valB = String(valB).toLowerCase();
    }

    if (valA < valB) return sortOrder === 'asc' ? -1 : 1;
    if (valA > valB) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const filteredCandidates = sortedCandidates.filter((candidate) => {
    const searchText = search.toLowerCase();

    return (
      String(candidate.candidateid || '').toLowerCase().includes(searchText) ||
      String(candidate.fullname || '').toLowerCase().includes(searchText) ||
      String(candidate.email || '').toLowerCase().includes(searchText) ||
      String(candidate.mobileNumber || '').toLowerCase().includes(searchText) ||
      String(candidate.dateOfBirth || '').toLowerCase().includes(searchText) ||
      String(candidate.caseInitiatedDateOnlyAsString || '').toLowerCase().includes(searchText) ||
      String(candidate.reportDateOnlyString || '').toLowerCase().includes(searchText) ||
      String(candidate.bgcStatus || '').toLowerCase().includes(searchText)
    );
  });

  const startIndex = totalElements === 0 ? 0 : currentPage * entriesPerPage + 1;
  const endIndex = totalElements === 0 ? 0 : Math.min((currentPage + 1) * entriesPerPage, totalElements);
  const totalPages = Math.ceil(totalElements / entriesPerPage);

  return (
    <>
      <p className="client-header">BGV Completed Candidates</p>
      <div className="client-wrapper">
        {fetchError && <div className="error-message">{fetchError}</div>}

        <div className="clinet-table-controls">
          <div className="entries-select">
            Show{' '}
            <select
              value={entriesPerPage}
              onChange={(e) => {
                setEntriesPerPage(Number(e.target.value));
                setCurrentPage(0);
              }}
            >
              <option value="10">10</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>{' '}
            entries
          </div>

          <div className="client-search-box">
            <div className="client-search-input-wrapper">
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="Search by any field..."
              />
              <Search className="client-search-icon" size={18} />
            </div>
          </div>
        </div>

        <div className="clinet-table-container">
          {loading ? (
            <div className="loading">Loading Candidates...</div>
          ) : (
            <table className="candidates-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort('candidateid')}>
                    Candidate ID{renderSortIcon('candidateid')}
                  </th>
                  <th onClick={() => handleSort('fullname')}>
                    Name{renderSortIcon('fullname')}
                  </th>
                  <th onClick={() => handleSort('email')}>
                    Email{renderSortIcon('email')}
                  </th>
                  <th onClick={() => handleSort('mobileNumber')}>
                    Mobile{renderSortIcon('mobileNumber')}
                  </th>
                  <th onClick={() => handleSort('dateOfBirth')}>
                    DOB{renderSortIcon('dateOfBirth')}
                  </th>
                  <th onClick={() => handleSort('caseInitiatedDateOnlyAsString')}>
                    Case Initiated Date{renderSortIcon('caseInitiatedDateOnlyAsString')}
                  </th>
                  <th onClick={() => handleSort('reportDateOnlyString')}>
                    BGV Completed Date{renderSortIcon('reportDateOnlyString')}
                  </th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredCandidates.length > 0 ? (
                  filteredCandidates.map((candidate) => (
                    <tr key={candidate.candidateid}>
                      <td>{candidate.candidateid}</td>
                      <td>{candidate.fullname}</td>
                      <td>{candidate.email}</td>
                      <td>{candidate.mobileNumber}</td>
                      <td>{candidate.dateOfBirth}</td>
                      <td>{candidate.caseInitiatedDateOnlyAsString || 'N/A'}</td>
                      <td>{candidate.reportDateOnlyString || 'N/A'}</td>
                      <td>
                        <button
                          className="icon-btn"
                          onClick={() => onViewFinalReport(candidate.candidateid)}
                          disabled={candidate.bgcStatus !== 'COMPLETED'}
                          title="Download PDF"
                        >
                          <FaDownload
                            className={
                              candidate.bgcStatus === 'COMPLETED'
                                ? 'text-purple-600'
                                : 'text-gray-400'
                            }
                          />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="no-data">
                      No matching candidates found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {totalElements > 0 && (
          <div className="pagination">
            <div className="pagination-summary">
              Showing {currentPage * entriesPerPage + 1} to {Math.min((currentPage + 1) * entriesPerPage, totalElements)} of {totalElements} entries
            </div>

            <div className="pagination-buttons-user">
              <button
                className="page-btn"
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 0))}
                disabled={currentPage === 0}
              >
                ←
              </button>

              <button
                className={`page-btn ${"active"}`}
                onClick={() => setCurrentPage(currentPage)}
              >
                {currentPage + 1}
              </button>

              {currentPage + 1 < totalPages && (
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  {currentPage + 2}
                </button>
              )}

              <button
                className="page-btn"
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1))}
                disabled={currentPage === totalPages - 1}
              >
                →
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default BgvCompletedCandidatesPage;
